import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TableInscricoes from "./componets/TableInscricoes";
import InscricaoDetalheDialog from "./componets/InscricaoDetalheDialog";

import { LojaPageContainer } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";

import { debouce, resolveApiMessage } from "helpers/utils";

import {
  IListInscricoesActive,
  TypeStatusInscricao,
} from "interfaces/inscricoes";
import {
  getAllInscricoesEventoAtivo,
  setBaixaPaymentoInscricao,
  setInscricaoCortesia,
} from "services/inscricoes";
import {
  getDetailBoleto,
  getDetailCartaoCredito,
  getDetailPix,
} from "services/payments";
import { IDetailsBoleto } from "interfaces/detailsBoleto";
import { IDetailsPix } from "interfaces/detailsPix";
import { IDetailsCartaoEfi } from "interfaces/detailsCartao";

type PaymentDetails = IDetailsBoleto | IDetailsPix | IDetailsCartaoEfi | null;

const ACCENT = "#F96030";

type StatusFilter = "" | TypeStatusInscricao;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "Pendente", label: "Pendentes" },
  { value: "Pago", label: "Pagas" },
  { value: "Cortesia", label: "Cortesia" },
  { value: "Cancelado", label: "Canceladas" },
];

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight="bold" color={accent ? ACCENT : "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function countByStatus(inscricoes: IListInscricoesActive[], status: TypeStatusInscricao) {
  return inscricoes.filter((i) => i.statusInscricao.tipo === status).length;
}

export default function Inscricoes() {
  const [, dispatch] = useMaterialUIController();
  const [isloading, setIsLoading] = useState<boolean>(true);
  const [openModalBaixaManualPay, setOpenModalBaixaManualPay] = useState<boolean>(false);
  const [openModalCortesia, setOpenModalCortesia] = useState<boolean>(false);
  const [openModalEditInscricao, setOpenModalEditInscricao] = useState<boolean>(false);
  const [selectedOptionEstatistica, setSelectedOptionEstatistica] =
    useState<TypeStatusInscricao | null>(null);
  const [isUpdate, setIsUpdate] = useState<number>(0);
  const [listInscricoes, setLIstInscricoes] = useState<IListInscricoesActive[]>([]);
  const [searchListInscricoes, setSearchListInscricoes] = useState<
    IListInscricoesActive[] | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInscricao, setSelectedInscricao] = useState<IListInscricoesActive | null>(null);
  const [detailsPayment, setDetailsPayment] = useState<PaymentDetails>(null);

  useEffect(() => {
    (async () => {
      const result = await getAllInscricoesEventoAtivo().finally(() => setIsLoading(false));
      if (result.status === 200) {
        setLIstInscricoes(result.data);
        setSearchListInscricoes(null);
        setSelectedOptionEstatistica(null);
        setSearchQuery("");
        setDetailsPayment(null);
      }
    })();
  }, [isUpdate]);

  const handleUpdate = () => setIsUpdate(new Date().getTime());

  const displayedList = searchListInscricoes ?? listInscricoes;
  const isFiltered = searchListInscricoes !== null || selectedOptionEstatistica !== null;

  const stats = useMemo(
    () => ({
      total: listInscricoes.length,
      pendentes: countByStatus(listInscricoes, "Pendente"),
      pagas:
        countByStatus(listInscricoes, "Pago") +
        countByStatus(listInscricoes, "Gratuito") +
        countByStatus(listInscricoes, "RecOrganizador"),
      canceladas: countByStatus(listInscricoes, "Cancelado"),
    }),
    [listInscricoes],
  );

  const handleDetailsPagamento = async (inscricao: IListInscricoesActive) => {
    setIsLoading(true);
    if (inscricao.pagamentos.cobrancaBoleto) {
      const { charge_id, pdf_change } = inscricao.pagamentos.cobrancaBoleto;
      const resultDetailsBolCard = pdf_change
        ? await getDetailBoleto(charge_id)
        : await getDetailCartaoCredito(charge_id);
      setDetailsPayment(resultDetailsBolCard);
    }

    if (inscricao.pagamentos.cobrancaPix) {
      const resultDetailsPix = await getDetailPix(inscricao.pagamentos.cobrancaPix.txid).finally(
        () => setIsLoading(false),
      );
      setDetailsPayment(resultDetailsPix);
    }
    setIsLoading(false);
  };

  const handleCloseDetalhe = () => {
    setOpenModalEditInscricao(false);
    setOpenModalBaixaManualPay(false);
    setOpenModalCortesia(false);
    setSelectedInscricao(null);
    setDetailsPayment(null);
  };

  const handleSelectedInscricao = (inscricao: IListInscricoesActive) => {
    setSelectedInscricao(inscricao);
    setDetailsPayment(null);
    setOpenModalEditInscricao(true);
  };

  const handlerOnChangeSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setIsLoading(true);
    if (!searchTerm.trim()) {
      setSearchListInscricoes(null);
      setIsLoading(false);
      return;
    }
    const filteredData = listInscricoes.filter((item) =>
      Object.values(item).some(
        (value) =>
          value != null && value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
    setSearchListInscricoes(filteredData);
    handleSelectOptionEstatistica(null);
    setIsLoading(false);
  };

  const debouncedHandler = useMemo(
    () => debouce(handlerOnChangeSearch, 400),
    [listInscricoes],
  );

  const applyStatusFilter = (status: TypeStatusInscricao | null) => {
    if (status) {
      const data = listInscricoes.filter((item) => item.statusInscricao.tipo === status);
      setSearchListInscricoes(data);
    } else {
      setSearchListInscricoes(null);
    }
  };

  const handleStatusFilterChange = (_: React.MouseEvent<HTMLElement>, next: StatusFilter | null) => {
    if (next === null) return;
    const status = next === "" ? null : next;
    setSelectedOptionEstatistica(status);
    applyStatusFilter(status);
    if (status) setSearchQuery("");
  };

  const handleBaixaPaymentInscricao = async (values: any) => {
    try {
      const { status, data } = await setBaixaPaymentoInscricao({
        inscricaoId: selectedInscricao!.id,
        obs: values.observacao,
      });

      if (status === 200) {
        setOpenModalBaixaManualPay(false);
        handleCloseDetalhe();
        handleUpdate();
        setMessage(dispatch, {
          open: true,
          text: "Pagamento da inscrição baixada com sucesso",
        });
      } else {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: resolveApiMessage(data, "Não foi possível baixar o pagamento da inscrição."),
        });
      }
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: resolveApiMessage(error?.message, "Não foi possível baixar o pagamento da inscrição."),
      });
    }
  };

  const handleSetCortesiaInscricao = async (values: any) => {
    try {
      const { data, status } = await setInscricaoCortesia({
        inscricaoId: selectedInscricao!.id,
        obs: values.observacao,
      });

      if (status === 200) {
        setMessage(dispatch, { open: true, text: "Cortesia concedida com sucesso" });
        setOpenModalCortesia(false);
        handleCloseDetalhe();
        handleUpdate();
      } else {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: resolveApiMessage(data, "Não foi possível conceder a cortesia."),
        });
      }
    } catch (error: any) {
      setMessage(dispatch, {
        open: true,
        text: resolveApiMessage(error?.message, "Não foi possível conceder a cortesia."),
        severity: "error",
      });
    }
  };

  const handleSelectOptionEstatistica = (value: TypeStatusInscricao | null) => {
    setSelectedOptionEstatistica(value);
  };

  const statusFilterValue: StatusFilter = selectedOptionEstatistica ?? "";

  return (
    <LojaPageContainer
      title="Inscrições"
      subtitle="Inscrições de todos os eventos com inscrições abertas."
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "Exibindo agora" : "Total de inscrições"}
            value={isFiltered ? displayedList.length : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Pendentes" value={stats.pendentes} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Confirmadas" value={stats.pagas} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Canceladas" value={stats.canceladas} />
        </Grid>
      </Grid>

      <MDBox
        mb={2}
        p={2}
        borderRadius="lg"
        sx={{
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${ACCENT}`,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HowToRegOutlinedIcon sx={{ color: ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isFiltered
                ? `${displayedList.length} inscrições no filtro atual`
                : "Todas as inscrições ativas"}
            </MDTypography>
          </Stack>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
          >
            <FormControl variant="outlined" size="small" sx={{ minWidth: { lg: 320 }, flex: 1 }}>
              <InputLabel htmlFor="inscricoes-search">Pesquisar inscrição</InputLabel>
              <OutlinedInput
                id="inscricoes-search"
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  debouncedHandler(e.target.value);
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                }
                label="Pesquisar inscrição"
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilterValue}
              onChange={handleStatusFilterChange}
              sx={{
                flexWrap: "wrap",
                "& .MuiToggleButton-root": {
                  px: 1.25,
                  py: 0.75,
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  "&.Mui-selected": {
                    bgcolor: "rgba(249, 96, 48, 0.1)",
                    borderColor: ACCENT,
                    color: ACCENT,
                    fontWeight: 600,
                  },
                },
              }}
            >
              {STATUS_FILTERS.map((opt) => (
                <ToggleButton key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </MDBox>

      {!isloading && displayedList.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <HowToRegOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhuma inscrição encontrada
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe os filtros de status."
              : "Não há inscrições em eventos ativos no momento."}
          </MDTypography>
        </MDBox>
      ) : (
        <TableInscricoes
          selectedInscricao={handleSelectedInscricao}
          isloading={isloading}
          listInscricoes={displayedList}
        />
      )}

      <InscricaoDetalheDialog
        open={openModalEditInscricao}
        inscricao={selectedInscricao}
        isloading={isloading}
        detailsPayment={detailsPayment}
        openModalBaixaManualPay={openModalBaixaManualPay}
        openModalCortesia={openModalCortesia}
        onClose={handleCloseDetalhe}
        onDetailsPagamento={handleDetailsPagamento}
        onToggleBaixaManual={() => setOpenModalBaixaManualPay(true)}
        onCloseBaixaManual={() => setOpenModalBaixaManualPay(false)}
        onToggleCortesia={() => setOpenModalCortesia(true)}
        onCloseCortesia={() => setOpenModalCortesia(false)}
        onSubmitBaixa={handleBaixaPaymentInscricao}
        onSubmitCortesia={handleSetCortesiaInscricao}
        onUpdated={handleUpdate}
      />
    </LojaPageContainer>
  );
}
